import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  User,
  Calendar,
  Loader2,
  X,
  Smartphone,
  MapPin,
  Copy,
} from "lucide-react";
import { toast } from "react-toastify";
import Navbar from "../../components/Navbar";
import promoService from "../../requests/admin/PromoService";

const MODEL_ID_TO_LABEL = {
  cyber16pro: "Hotwav Cyber 16 Pro",
  a17promax: "Hotwav A17 Pro Max",
};

const normalizeModelId = (id) => String(id || "").trim().toLowerCase().replace(/\s+/g, "");

const PromoSubmissions = () => {
  const [promos, setPromos] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimerRef = useRef(null);
  const [pagination, setPagination] = useState({
    total: 0,
    per_page: 15,
    current_page: 1,
    last_page: 1,
    from: 0,
    to: 0,
  });
  const [locationModal, setLocationModal] = useState({ open: false, location: "", name: "" });

  const loadPromos = useCallback(async () => {
    setIsLoading(true);
    setIsSearching(true);
    try {
      const result = await promoService.getAllPromos(
        currentPage,
        itemsPerPage,
        searchTerm,
        sortOrder,
        dateFrom,
        dateTo
      );
      if (result.status) {
        setPromos(result.data || []);
        if (result.pagination && Object.keys(result.pagination).length) {
          setPagination(result.pagination);
        }
      } else {
        toast.error(result.message || "Failed to load promo submissions");
      }
    } catch (error) {
      console.error("Error loading promos:", error);
      toast.error("An error occurred while loading promo submissions");
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, sortOrder, dateFrom, dateTo]);

  useEffect(() => {
    loadPromos();
  }, [loadPromos]);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    const trimmed = searchInput.trim();
    if (trimmed === "" && searchTerm !== "") {
      setSearchTerm("");
      setCurrentPage(1);
      return;
    }
    if (trimmed !== "") {
      debounceTimerRef.current = setTimeout(() => {
        setSearchTerm(trimmed);
        setCurrentPage(1);
      }, 400);
    }
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce: only react to searchInput
  }, [searchInput]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const handleDateFilterApply = () => {
    const fromEl = document.getElementById("promo-dateFrom");
    const toEl = document.getElementById("promo-dateTo");
    setDateFrom(fromEl?.value || "");
    setDateTo(toEl?.value || "");
    setShowDateFilter(false);
    setCurrentPage(1);
  };

  const handleDateFilterClear = () => {
    setDateFrom("");
    setDateTo("");
    setShowDateFilter(false);
    setCurrentPage(1);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showDateFilter && !e.target.closest(".promo-date-filter-container")) {
        setShowDateFilter(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDateFilter]);

  const total = pagination.total || 0;
  const perPage = pagination.per_page || itemsPerPage;
  const currentPageNum = pagination.current_page || currentPage;
  const totalPages = Math.max(1, pagination.last_page || Math.ceil(total / perPage));
  const showingFrom = pagination.from || (currentPageNum - 1) * perPage + 1;
  const showingTo = Math.min(pagination.to || currentPageNum * perPage, total);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatModelDisplay = (model) => {
    if (model == null || model === "") return "";
    const normalized = normalizeModelId(model);
    return MODEL_ID_TO_LABEL[normalized] || String(model).trim() || "—";
  };

  return (
    <>
      <Navbar page="Promo Submissions" />
      <div className="min-h-screen bg-gray-50">
        <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-[#FF6C6C] blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-[#FF8787] blur-3xl" />
          </div>
          <div className="relative w-full min-w-0 px-4 py-5 mx-auto max-w-7xl sm:px-6 sm:py-6 md:px-8 md:py-7 lg:px-12 lg:py-8">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
              <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4 md:gap-5">
                <div className="relative flex flex-shrink-0 items-center justify-center">
                  <div className="relative h-[100px] w-14 rounded-[16px] bg-gradient-to-b from-gray-700 to-gray-800 border-2 border-white/20 shadow-2xl overflow-hidden sm:h-[120px] sm:w-16 md:h-[140px] md:w-20 md:rounded-[20px]">
                    <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-8 h-1 rounded-full bg-gray-600 sm:w-10 md:top-2 md:w-12 md:h-1.5" />
                    <div className="flex h-full w-full items-center justify-center p-2 pt-5 sm:p-2.5 sm:pt-6 md:p-3 md:pt-6">
                      <Smartphone className="h-7 w-7 text-white/90 sm:h-8 sm:w-8 md:h-10 md:w-10" strokeWidth={1.5} />
                    </div>
                    <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 h-1 w-10 rounded-full bg-gray-600/80 sm:w-12 md:bottom-2 md:w-14" />
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <h1 className="text-xl font-bold tracking-tight sm:text-2xl md:text-3xl truncate sm:whitespace-normal">
                    Hotwav Pre-orders
                  </h1>
                  <p className="mt-0.5 text-xs text-white/80 sm:mt-1 sm:text-sm md:text-base">
                    Cyber 16 Pro & A17 Pro Max — submission list
                  </p>
                </div>
              </div>
              <div className="flex flex-shrink-0 flex-col items-center sm:items-end">
                <span className="text-3xl font-bold tabular-nums tracking-tight text-white sm:text-4xl md:text-5xl">
                  {total}
                </span>
                <span className="mt-0.5 text-xs text-white/80 sm:text-sm">Total pre-orders</span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 mx-auto max-w-7xl sm:px-8 lg:px-12">
        <div className="p-6 mb-6 bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-1 min-w-0">
              <div className="relative flex-1 min-w-0 sm:max-w-[280px]">
                <Search
                  className={`absolute left-3 top-1/2 w-5 h-5 -translate-y-1/2 ${
                    isSearching ? "text-[#FF6C6C] animate-pulse" : "text-gray-400"
                  }`}
                />
                <input
                  type="text"
                  placeholder="Search by name, email, phone..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-200 outline-none text-sm"
                />
                {searchInput && searchInput !== searchTerm && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Loader2 className="w-4 h-4 text-[#FF6C6C] animate-spin" />
                  </div>
                )}
              </div>

              <div className="relative promo-date-filter-container flex items-center gap-3">
                <button
                  onClick={() => setShowDateFilter(!showDateFilter)}
                  className={`inline-flex items-center gap-2 px-3 py-2.5 border rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    dateFrom || dateTo
                      ? "border-[#FF6C6C] bg-[#FF6C6C]/5 text-[#FF6C6C]"
                      : "border-gray-300 hover:border-[#FF6C6C] hover:bg-gray-50 text-gray-700"
                  }`}
                >
                  <Calendar className="w-4 h-4 shrink-0" />
                  {dateFrom || dateTo ? "Date filtered" : "Filter by date"}
                  {(dateFrom || dateTo) && (
                    <X
                      size={14}
                      className="shrink-0 hover:bg-[#FF6C6C]/10 rounded-full p-0.5"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDateFilterClear();
                      }}
                    />
                  )}
                </button>

                {showDateFilter && (
                  <div className="absolute top-full left-0 mt-2 w-full sm:w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-4 promo-date-filter-container">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-sm font-semibold text-gray-900">Filter by date range</h3>
                      <button
                        onClick={() => setShowDateFilter(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                        <input
                          id="promo-dateFrom"
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] outline-none text-sm"
                          max={dateTo || undefined}
                          defaultValue={dateFrom}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                        <input
                          id="promo-dateTo"
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] outline-none text-sm"
                          min={dateFrom || undefined}
                          defaultValue={dateTo}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={handleDateFilterClear}
                        className="px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Clear
                      </button>
                      <button
                        onClick={handleDateFilterApply}
                        className="px-3 py-2 text-sm font-medium text-white bg-[#FF6C6C] rounded-lg hover:bg-[#FF5555]"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 whitespace-nowrap">Sort</span>
                <select
                  value={sortOrder}
                  onChange={(e) => {
                    setSortOrder(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] outline-none"
                >
                  <option value="desc">Newest first</option>
                  <option value="asc">Oldest first</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 whitespace-nowrap">Per page</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] outline-none min-w-[70px]"
                >
                  {[10, 15, 20, 30, 50].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-hidden bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[920px]">
              <thead className="sticky top-0 z-10 bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="w-[72px] px-4 py-4 text-right text-xs font-semibold tracking-wider text-gray-600 uppercase align-middle">
                    ID
                  </th>
                  <th className="min-w-[160px] max-w-[200px] px-4 py-4 text-left text-xs font-semibold tracking-wider text-gray-600 uppercase align-middle">
                    Name
                  </th>
                  <th className="min-w-[130px] px-4 py-4 text-left text-xs font-semibold tracking-wider text-gray-600 uppercase align-middle">
                    Phone
                  </th>
                  <th className="min-w-[180px] max-w-[220px] px-4 py-4 text-left text-xs font-semibold tracking-wider text-gray-600 uppercase align-middle">
                    Email
                  </th>
                  <th className="min-w-[120px] max-w-[180px] px-4 py-4 text-left text-xs font-semibold tracking-wider text-gray-600 uppercase align-middle">
                    Location
                  </th>
                  <th className="min-w-[240px] px-4 py-4 text-left text-xs font-semibold tracking-wider text-gray-600 uppercase align-middle">
                    Phone model
                  </th>
                  <th className="w-[150px] min-w-[150px] px-4 py-4 text-right text-xs font-semibold tracking-wider text-gray-600 uppercase align-middle">
                    Submitted
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {isLoading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-16 text-center">
                      <div className="flex justify-center items-center">
                        <Loader2 className="w-8 h-8 text-[#FF6C6C] animate-spin" />
                      </div>
                    </td>
                  </tr>
                ) : promos.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-16 text-center text-gray-500">
                      No promo submissions found
                    </td>
                  </tr>
                ) : (
                  promos.map((promo) => (
                    <motion.tr
                      key={promo.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="transition-colors hover:bg-gray-50/80 border-b border-gray-100 last:border-b-0"
                    >
                      <td className="px-4 py-3.5 text-right text-sm font-medium text-gray-900 tabular-nums align-middle">
                        {promo.id}
                      </td>
                      <td className="px-4 py-3.5 align-middle">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] flex items-center justify-center">
                            <User className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-sm font-medium text-gray-900 truncate" title={promo.name || ""}>
                            {promo.name || "—"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 align-middle">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm text-gray-700 font-mono tabular-nums truncate">
                            {promo.phone_number || "—"}
                          </span>
                          {promo.phone_number && (
                            <button
                              type="button"
                              onClick={() => {
                                navigator.clipboard.writeText(promo.phone_number).then(
                                  () => toast.success("Phone number copied"),
                                  () => toast.error("Could not copy")
                                );
                              }}
                              className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-[#FF6C6C] hover:bg-[#FF6C6C]/10 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20"
                              title="Copy phone number"
                              aria-label="Copy phone number"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-700 truncate max-w-[220px] align-middle" title={promo.email || ""}>
                        {promo.email || "—"}
                      </td>
                      <td className="px-4 py-3.5 align-middle max-w-[180px]">
                        {promo.location ? (
                          <button
                            type="button"
                            onClick={() => setLocationModal({ open: true, location: promo.location, name: promo.name || "" })}
                            className="w-full text-left text-sm text-gray-700 truncate block hover:text-[#FF6C6C] focus:text-[#FF6C6C] focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 rounded px-1 -mx-1 py-0.5"
                            title="View full location"
                          >
                            {promo.location}
                          </button>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 align-middle min-w-[240px]">
                        {(!Array.isArray(promo.models) || promo.models.length === 0) ? (
                          <span className="text-sm text-gray-400">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-2" title={promo.models.map(formatModelDisplay).filter(Boolean).join(", ")}>
                            {promo.models.map((model, i) => {
                              const label = formatModelDisplay(model);
                              if (!label) return null;
                              return (
                                <span
                                  key={i}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-[#FF6C6C]/10 text-[#FF6C6C] border border-[#FF6C6C]/25 shadow-sm"
                                >
                                  <Smartphone className="w-3.5 h-3.5 shrink-0 opacity-80" />
                                  {label}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-right text-sm text-gray-600 whitespace-nowrap tabular-nums align-middle">
                        {formatDate(promo.created_at)}
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {promos.length > 0 && totalPages > 1 && (
            <div className="flex flex-col gap-4 px-6 py-4 border-t border-gray-200 sm:flex-row sm:justify-between sm:items-center">
              <div className="flex gap-2 items-center">
                <span className="text-sm text-gray-600">
                  Showing {showingFrom} to {showingTo} of {total} entries
                </span>
              </div>
              <div className="flex gap-2 items-center">
                <button
                  onClick={() => handlePageChange(currentPageNum - 1)}
                  disabled={currentPageNum === 1}
                  className={`p-2 rounded-lg transition-colors ${
                    currentPageNum === 1
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-600 hover:bg-gray-100 hover:text-[#FF6C6C]"
                  }`}
                  title="Previous page"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex gap-1 items-center">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (currentPageNum <= 3) pageNum = i + 1;
                    else if (currentPageNum >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = currentPageNum - 2 + i;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                          currentPageNum === pageNum
                            ? "bg-[#FF6C6C] text-white"
                            : "text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => handlePageChange(currentPageNum + 1)}
                  disabled={currentPageNum === totalPages}
                  className={`p-2 rounded-lg transition-colors ${
                    currentPageNum === totalPages
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-600 hover:bg-gray-100 hover:text-[#FF6C6C]"
                  }`}
                  title="Next page"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>

      {locationModal.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setLocationModal((prev) => ({ ...prev, open: false }))}
          role="dialog"
          aria-modal="true"
          aria-labelledby="location-modal-title"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-[#FF6C6C]/5 to-transparent">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#FF6C6C]/10">
                <MapPin className="w-6 h-6 text-[#FF6C6C]" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 id="location-modal-title" className="text-lg font-semibold text-gray-900">
                  Location
                </h2>
                {locationModal.name && (
                  <p className="text-sm text-gray-500 truncate">{locationModal.name}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setLocationModal((prev) => ({ ...prev, open: false }))}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-700 leading-relaxed break-words whitespace-pre-wrap">
                {locationModal.location}
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default PromoSubmissions;
